import api from './api';
import type {
  Workflow,
  WorkflowListItem,
} from '../types/workflow';

const API_BASE = '/workflows';

// 获取工作流列表
export const getWorkflows = async (): Promise<WorkflowListItem[]> => {
  try {
    const response = await api.get<{ data: WorkflowListItem[]; meta?: unknown }>(API_BASE);
    // 后端返回格式: { data: [...], meta: {...} }
    if (response.data && Array.isArray(response.data.data)) {
      return response.data.data;
    }
    throw new Error('获取工作流列表失败: 响应格式错误');
  } catch (error) {
    console.error('获取工作流列表失败:', error);
    // 返回本地存储的数据作为后备
    return getLocalWorkflows();
  }
};

// 获取单个工作流
export const getWorkflow = async (id: string): Promise<Workflow> => {
  try {
    const response = await api.get<Workflow>(`${API_BASE}/${id}`);
    if (response.data) {
      return response.data;
    }
    throw new Error('获取工作流失败: 响应格式错误');
  } catch (error) {
    console.error('获取工作流失败:', error);
    // 尝试从本地存储获取
    const local = getLocalWorkflowById(id);
    if (local) return local;
    throw error;
  }
};

// 创建工作流
export const createWorkflow = async (
  workflow: Omit<Workflow, 'id' | 'createdAt' | 'updatedAt'>
): Promise<Workflow> => {
  try {
    const response = await api.post<Workflow>(API_BASE, workflow);
    if (response.data) {
      // 更新本地存储
      saveWorkflowToLocal(response.data);
      return response.data;
    }
    throw new Error('创建工作流失败: 响应格式错误');
  } catch (error) {
    console.error('创建工作流失败:', error);
    // 本地创建
    const newWorkflow: Workflow = {
      ...workflow,
      id: generateLocalId(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    saveWorkflowToLocal(newWorkflow);
    return newWorkflow;
  }
};

// 更新工作流
export const updateWorkflow = async (
  id: string,
  updates: Partial<Workflow>
): Promise<Workflow> => {
  try {
    const response = await api.put<Workflow>(
      `${API_BASE}/${id}`,
      updates
    );
    if (response.data) {
      saveWorkflowToLocal(response.data);
      return response.data;
    }
    throw new Error('更新工作流失败: 响应格式错误');
  } catch (error: any) {
    console.error('更新工作流失败:', error);
    // 如果是401错误，直接抛出让上层处理
    if (error.response?.status === 401) {
      throw new Error('401: 登录已过期，请重新登录');
    }
    // 本地更新作为后备
    const existing = getLocalWorkflowById(id);
    if (existing) {
      const updated = { ...existing, ...updates, updatedAt: Date.now() };
      saveWorkflowToLocal(updated);
      return updated;
    }
    throw error;
  }
};

// 删除工作流
export const deleteWorkflow = async (id: string): Promise<void> => {
  try {
    await api.delete(`${API_BASE}/${id}`);
  } catch (error) {
    console.error('删除工作流失败:', error);
  } finally {
    // 始终删除本地存储
    deleteLocalWorkflow(id);
  }
};

// 执行工作流
export const executeWorkflow = async (
  id: string,
  variables?: Record<string, unknown>
): Promise<{ executionId: string }> => {
  const response = await api.post<{ executionId: string }>(
    `${API_BASE}/${id}/execute`,
    { variables }
  );
  if (response.data) {
    return response.data;
  }
  throw new Error('执行工作流失败: 响应格式错误');
};

// 获取工作流执行状态
export const getWorkflowExecution = async (executionId: string) => {
  const response = await api.get<unknown>(
    `${API_BASE}/executions/${executionId}`
  );
  if (response.data) {
    return response.data;
  }
  throw new Error('获取执行状态失败: 响应格式错误');
};

// ==================== 本地存储方法 ====================

const LOCAL_STORAGE_KEY = 'workflows';

const generateLocalId = (): string => {
  return `local-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

const getLocalWorkflows = (): WorkflowListItem[] => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const workflows: Workflow[] = JSON.parse(data);
      return workflows.map((w) => ({
        id: w.id,
        name: w.name,
        description: w.description,
        createdAt: w.createdAt,
        updatedAt: w.updatedAt,
        version: w.version,
        isActive: w.isActive,
        nodeCount: w.nodes.length,
      }));
    }
  } catch (error) {
    console.error('读取本地工作流失败:', error);
  }
  return [];
};

const getLocalWorkflowById = (id: string): Workflow | null => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const workflows: Workflow[] = JSON.parse(data);
      return workflows.find((w) => w.id === id) || null;
    }
  } catch (error) {
    console.error('读取本地工作流失败:', error);
  }
  return null;
};

const saveWorkflowToLocal = (workflow: Workflow): void => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    const workflows: Workflow[] = data ? JSON.parse(data) : [];
    const index = workflows.findIndex((w) => w.id === workflow.id);
    if (index >= 0) {
      workflows[index] = workflow;
    } else {
      workflows.push(workflow);
    }
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(workflows));
  } catch (error) {
    console.error('保存工作流到本地失败:', error);
  }
};

const deleteLocalWorkflow = (id: string): void => {
  try {
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (data) {
      const workflows: Workflow[] = JSON.parse(data);
      const filtered = workflows.filter((w) => w.id !== id);
      localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(filtered));
    }
  } catch (error) {
    console.error('删除本地工作流失败:', error);
  }
};

// 导出本地存储方法供直接使用
export const workflowLocalStorage = {
  getAll: getLocalWorkflows,
  getById: getLocalWorkflowById,
  save: saveWorkflowToLocal,
  delete: deleteLocalWorkflow,
};
